/**
 * V1 Baseline Calibration Gate
 *
 * Per V1 Product Spec:
 * - All 5 foundational lifts must be logged within the freshness window before duels unlock.
 * - To initiate a duel: ≥4 lifts fresh or in grace.
 * - To accept a duel: no restrictions (but must have baseline).
 * - Leaderboard inclusion requires baseline completion.
 *
 * Invariant #4: Baseline calibration is required for ranked participation.
 */

import { V1_LIFT_POOL, V1_LIFT_LABELS, type V1Lift } from '@/lib/lifts/canonical';

/** Freshness windows in days (from V1 spec) */
export const FRESHNESS = {
  FRESH_MAX_DAYS: 28,
  GRACE_MAX_DAYS: 42,
  STALE_MAX_DAYS: 56,
} as const;

export type FreshnessStatus = 'fresh' | 'aging' | 'stale' | 'expired' | 'missing';

export interface LiftFreshness {
  lift: V1Lift;
  label: string;
  lastLoggedAt: string | null;
  daysSinceLog: number | null;
  status: FreshnessStatus;
  /** Contribution multiplier: 1.0 (fresh), decay to 0.7 (aging), decay to 0.4 (stale), 0 (expired) */
  contributionMultiplier: number;
}

export interface BaselineStatus {
  /** Whether all 5 lifts have been logged at least once within freshness window */
  isComplete: boolean;
  /** Number of lifts with at least one log (regardless of freshness) */
  liftsLoggedEver: number;
  /** Number of lifts currently fresh or in grace (days ≤42) */
  liftsFreshOrGrace: number;
  /** Number of lifts currently fresh (days ≤28) */
  liftsFresh: number;
  /** Per-lift status */
  lifts: LiftFreshness[];
  /** Can initiate a duel (≥4 lifts fresh/grace + baseline complete) */
  canInitiateDuel: boolean;
  /** Can accept a duel (baseline complete) */
  canAcceptDuel: boolean;
  /** Can appear on leaderboards (baseline complete) */
  canAppearOnLeaderboard: boolean;
}

/**
 * Calculate freshness status for a single lift.
 */
export function calculateLiftFreshness(
  lift: V1Lift,
  lastLoggedAt: string | null,
  now: Date = new Date()
): LiftFreshness {
  const label = V1_LIFT_LABELS[lift];

  if (!lastLoggedAt) {
    return {
      lift,
      label,
      lastLoggedAt: null,
      daysSinceLog: null,
      status: 'missing',
      contributionMultiplier: 0,
    };
  }

  const logDate = new Date(lastLoggedAt);
  const daysSinceLog = Math.floor((now.getTime() - logDate.getTime()) / (1000 * 60 * 60 * 24));

  let status: FreshnessStatus;
  let contributionMultiplier: number;

  if (daysSinceLog <= FRESHNESS.FRESH_MAX_DAYS) {
    status = 'fresh';
    contributionMultiplier = 1.0;
  } else if (daysSinceLog <= FRESHNESS.GRACE_MAX_DAYS) {
    // Linear decay from 1.0 to 0.7 over days 29–42
    const graceDays = daysSinceLog - FRESHNESS.FRESH_MAX_DAYS;
    const graceWindow = FRESHNESS.GRACE_MAX_DAYS - FRESHNESS.FRESH_MAX_DAYS;
    contributionMultiplier = 1.0 - (0.3 * (graceDays / graceWindow));
    status = 'aging';
  } else if (daysSinceLog <= FRESHNESS.STALE_MAX_DAYS) {
    // Linear decay from 0.7 to 0.4 over days 43–56
    const staleDays = daysSinceLog - FRESHNESS.GRACE_MAX_DAYS;
    const staleWindow = FRESHNESS.STALE_MAX_DAYS - FRESHNESS.GRACE_MAX_DAYS;
    contributionMultiplier = 0.7 - (0.3 * (staleDays / staleWindow));
    status = 'stale';
  } else {
    status = 'expired';
    contributionMultiplier = 0;
  }

  return {
    lift,
    label,
    lastLoggedAt,
    daysSinceLog,
    status,
    contributionMultiplier: Math.round(contributionMultiplier * 100) / 100,
  };
}

/**
 * Calculate full baseline status from per-lift last-logged dates.
 */
export function calculateBaselineStatus(
  liftDates: Partial<Record<V1Lift, string | null>>,
  now: Date = new Date()
): BaselineStatus {
  const lifts: LiftFreshness[] = V1_LIFT_POOL.map((lift) =>
    calculateLiftFreshness(lift, liftDates[lift] ?? null, now)
  );

  const liftsLoggedEver = lifts.filter((l) => l.lastLoggedAt !== null).length;
  const liftsFreshOrGrace = lifts.filter(
    (l) => l.status === 'fresh' || l.status === 'aging'
  ).length;
  const liftsFresh = lifts.filter((l) => l.status === 'fresh').length;

  // Baseline requires all 5 lifts logged and not expired
  const isComplete = lifts.every(
    (l) => l.status === 'fresh' || l.status === 'aging' || l.status === 'stale'
  );

  // Initiate duel: all 5 lifts logged at least once (ever). After first baseline
  // completion, users can always create duels regardless of freshness.
  const canInitiateDuel = liftsLoggedEver >= 5;

  // Accept duel: baseline complete
  const canAcceptDuel = isComplete;

  // Leaderboard: baseline complete
  const canAppearOnLeaderboard = isComplete;

  return {
    isComplete,
    liftsLoggedEver,
    liftsFreshOrGrace,
    liftsFresh,
    lifts,
    canInitiateDuel,
    canAcceptDuel,
    canAppearOnLeaderboard,
  };
}

/**
 * Get a human-readable explanation of why a user can't perform an action.
 */
export function getBaselineBlockReason(status: BaselineStatus, action: 'duel_initiate' | 'duel_accept' | 'leaderboard'): string | null {
  if (action === 'duel_initiate' && status.canInitiateDuel) return null;
  if (action === 'duel_accept' && status.canAcceptDuel) return null;
  if (action === 'leaderboard' && status.canAppearOnLeaderboard) return null;

  const missingLifts = status.lifts
    .filter((l) => l.status === 'missing' || l.status === 'expired')
    .map((l) => l.label);

  if (missingLifts.length > 0) {
    return `Complete your baseline: log ${missingLifts.join(', ')} to unlock ${action === 'leaderboard' ? 'leaderboards' : 'duels'}.`;
  }

  if (action === 'duel_initiate' && status.liftsFreshOrGrace < 4) {
    return `You need at least 4 fresh lifts to initiate a duel. Currently ${status.liftsFreshOrGrace}/4 are fresh or in grace.`;
  }

  return 'Complete your baseline calibration to continue.';
}
